//Per-Forest Monitoring Service

import { updateTreeGrowthStages, getForestTrees } from './firestore';
import { activityManager } from './activityManager';

interface ForestMonitoringState {
  forestId: string;
  userId: string;
  intervals: Map<string, NodeJS.Timeout>;
  isActive: boolean;
}

class PerForestMonitorService {
  private activeMonitors: Map<string, ForestMonitoringState> = new Map();
  private readonly POLLING_INTERVALS = {
    TREES: 30000,
    USER_STATS: 10000,
    OTHER_USERS: 1000,
    EXERCISES: 180000,
    PRESENCE_CLEANUP: 300000,
  };

 //Start monitoring a specific forest for a specific user

  startForestMonitoring(forestId: string, userId: string): void {
    const monitorKey = `${forestId}-${userId}`;
    
    //Stop existing user monitoring
    this.stopForestMonitoring(forestId, userId);
    
    console.log(`Starting per-forest monitoring for user ${userId} in forest ${forestId}`);
    
    const monitorState: ForestMonitoringState = {
      forestId,
      userId,
      intervals: new Map(),
      isActive: true
    };

    //Is user active
    if (activityManager.isActive()) {
      this.startPollingServices(monitorState);
    } else {
      console.log(`User inactive, monitoring will start when activity detected`);
    }

    this.activeMonitors.set(monitorKey, monitorState);
    
    //Listener
    this.setupActivityListener(monitorState);
  }

 
   //Stop monitoring user

  stopForestMonitoring(forestId: string, userId: string): void {
    const monitorKey = `${forestId}-${userId}`;
    const monitor = this.activeMonitors.get(monitorKey);
    
    if (monitor) {
      console.log(`Stopping per-forest monitoring for user ${userId} in forest ${forestId}`);
      this.stopPollingServices(monitor);
      this.activeMonitors.delete(monitorKey);
    }
  }

  //Stop all monitoring
  stopAllMonitoring(): void {
    console.log(`Stopping all forest monitoring`);
    this.activeMonitors.forEach((monitor) => {
      this.stopPollingServices(monitor);
    });
    this.activeMonitors.clear();
  }

  //Start monitor polling
  private startPollingServices(monitor: ForestMonitoringState): void {
    if (!monitor.isActive) return;
    
    console.log(`Starting polling services for forest ${monitor.forestId}`);
    
    //Tree growth
    const treeInterval = setInterval(async () => {
      if (!activityManager.isActive()) {
        console.log(`User inactive, pausing tree updates for forest ${monitor.forestId}`);
        this.pausePollingServices(monitor);
        return;
      }
      
      try {
        await updateTreeGrowthStages(monitor.forestId);
      } catch (error) {
        console.error(`Error updating trees for forest ${monitor.forestId}:`, error);
      }
    }, this.POLLING_INTERVALS.TREES);
    
    monitor.intervals.set('trees', treeInterval);

    //User stats
    const statsInterval = setInterval(async () => {
      if (!activityManager.isActive()) {
        this.pausePollingServices(monitor);
        return;
      }
      
      try {
        await this.updateUserStats(monitor.forestId, monitor.userId);
      } catch (error) {
        console.error(`Error updating user stats for forest ${monitor.forestId}:`, error);
      }
    }, this.POLLING_INTERVALS.USER_STATS);
    
    monitor.intervals.set('stats', statsInterval);

    const cleanupInterval = setInterval(async () => {
      if (!activityManager.isActive()) {
        this.pausePollingServices(monitor);
        return;
      }
      
      try {
        const { cleanupInactiveUsers } = await import('./userPresenceService');
        await cleanupInactiveUsers(monitor.forestId);
      } catch (error) {
        console.error(`Error cleaning up inactive users for forest ${monitor.forestId}:`, error);
      }
    }, this.POLLING_INTERVALS.PRESENCE_CLEANUP);
    
    monitor.intervals.set('presenceCleanup', cleanupInterval);

    console.log(`Started ${monitor.intervals.size} polling services for forest ${monitor.forestId}`);
  }

  //Stop polling
  private stopPollingServices(monitor: ForestMonitoringState): void {
    monitor.intervals.forEach((interval, serviceName) => {
      clearInterval(interval);
      console.log(`Stopped ${serviceName} polling for forest ${monitor.forestId}`);
    });
    monitor.intervals.clear();
    monitor.isActive = false;
  }

  //Pause on inactive
  private pausePollingServices(monitor: ForestMonitoringState): void {
    if (monitor.intervals.size === 0) return;
    
    console.log(`Pausing polling services for forest ${monitor.forestId} (user inactive)`);
    this.stopPollingServices(monitor);
  }

  //Resume on active
  private resumePollingServices(monitor: ForestMonitoringState): void {
    if (monitor.intervals.size > 0) return;
    
    console.log(`Resuming polling services for forest ${monitor.forestId} (user active)`);
    monitor.isActive = true;
    this.startPollingServices(monitor);
  }

  //Monitor activity listener
  private setupActivityListener(monitor: ForestMonitoringState): void {
    //Check activity status
    const activityCheck = setInterval(() => {
      const isUserActive = activityManager.isActive();
      const hasActivePolling = monitor.intervals.size > 0;
      
      if (isUserActive && !hasActivePolling) {
        console.log(`User became active, resuming monitoring for forest ${monitor.forestId}`);
        this.resumePollingServices(monitor);
      } else if (!isUserActive && hasActivePolling) {
        console.log(`User became inactive, pausing monitoring for forest ${monitor.forestId}`);
        this.pausePollingServices(monitor);
      }
    }, 30000);

    monitor.intervals.set('activityCheck', activityCheck);
  }

  //Update user stats
  private async updateUserStats(forestId: string, userId: string): Promise<void> {
    try {
      const { getUserWaterActions } = await import('./firestore');
      
      //Water stats
      const waterActions = await getUserWaterActions(forestId, userId);
      const totalReceived = waterActions.filter(action => action.fromUserId !== userId).length;
    
      const event = new CustomEvent('waterStatsUpdate', { 
        detail: { userId, forestId, totalReceived } 
      });
      window.dispatchEvent(event);
      
    } catch (error) {
      console.error('Error updating user stats:', error);
    }
  }

  //status
  getMonitoringStatus(): { 
    activeMonitors: number; 
    totalPollingServices: number;
    monitorDetails: Array<{
      forestId: string;
      userId: string;
      isActive: boolean;
      serviceCount: number;
    }>;
  } {
    let totalServices = 0;
    const details = Array.from(this.activeMonitors.values()).map(monitor => {
      totalServices += monitor.intervals.size;
      return {
        forestId: monitor.forestId,
        userId: monitor.userId,
        isActive: monitor.isActive,
        serviceCount: monitor.intervals.size
      };
    });

    return {
      activeMonitors: this.activeMonitors.size,
      totalPollingServices: totalServices,
      monitorDetails: details
    };
  }
}

//Export
export const perForestMonitor = new PerForestMonitorService();

//Helpers
export const startMonitoringCurrentForest = (forestId: string, userId: string): void => {
  perForestMonitor.startForestMonitoring(forestId, userId);
};

export const stopMonitoringCurrentForest = (forestId: string, userId: string): void => {
  perForestMonitor.stopForestMonitoring(forestId, userId);
};

export const getMonitoringStatus = () => {
  return perForestMonitor.getMonitoringStatus();
};